import { Feature } from 'ol';
import { fromLonLat } from 'ol/proj';
import sourceVector from 'ol/source/Vector.js';
import layerVector from 'ol/layer/Vector.js';
import LineString from 'ol/geom/LineString.js';
import Circle from 'ol/geom/Circle.js';
import { Style, Stroke } from 'ol/style.js';

const EARTH_RADIUS_KM = 6371.22;
const CURRENT_COLOR = 'red';
const PAST_COLOR = 'gray';
const PENDING_RADIUS_M = 2000;
// Le point courant doit toujours être visible par-dessus les points passés,
// peu importe lequel a été ajouté en dernier à la carte (l'ordre d'ajout des
// couches OpenLayers, pas l'ordre chronologique des clics, détermine sinon
// le z-order).
const PAST_ZINDEX = 1;
const CURRENT_ZINDEX = 2;

export function distanceKm(lat1, lon1, lat2, lon2) {
  const deg2rad = Math.PI / 180;
  return Math.acos(
    Math.sin(lat1 * deg2rad) * Math.sin(lat2 * deg2rad) +
    Math.cos(lat1 * deg2rad) * Math.cos(lat2 * deg2rad) * Math.cos((lon2 - lon1) * deg2rad)
  ) * EARTH_RADIUS_KM;
}

// Dessine un maillage 3x3 (lignes) centré sur (lon0, lat0) + un petit cercle
// central, à l'échelle réelle des axes de grille du modèle (axisU/axisV,
// trouvés par wms-forecast-client/src/gridAxes.js — pas nécessairement
// est/nord, cf. grille tournée HRDPS/RDPS). Porté de
// wms_time_series_with_WMS_API/modules/series.js (fonction gridMesh).
function meshFeatures(lon0, lat0, axisU, axisV, color) {
  const style = new Style({
    stroke: new Stroke({ color: color, width: 2 }),
  });
  const at = (i, j) => [
    lon0 + i * axisU[0] + j * axisV[0],
    lat0 + i * axisU[1] + j * axisV[1],
  ];
  const lines = [];
  for (let j = -1; j <= 1; j++) {
    lines.push([at(-1, j), at(1, j)]);
  }
  for (let i = -1; i <= 1; i++) {
    lines.push([at(i, -1), at(i, 1)]);
  }
  const features = lines.map(coords => {
    const feature = new Feature({
      geometry: new LineString(coords.map(c => fromLonLat(c))),
    });
    feature.setStyle(style);
    return feature;
  });

  // Cercle au point central, diamètre = une fraction de la plus petite
  // distance de grille, mesurée en coordonnées projetées le long de l'axe
  // le plus court pour rester correcte à l'échelle réelle peu importe la
  // latitude.
  const DIAMETER_FRACTION = 0.7;
  const shortestAxis = Math.hypot(...axisU) <= Math.hypot(...axisV) ? axisU : axisV;
  const centerProj = fromLonLat([lon0, lat0]);
  const edgeProj = fromLonLat([
    lon0 + (DIAMETER_FRACTION / 2) * shortestAxis[0],
    lat0 + (DIAMETER_FRACTION / 2) * shortestAxis[1],
  ]);
  const radius = Math.hypot(edgeProj[0] - centerProj[0], edgeProj[1] - centerProj[1]);
  const circleFeature = new Feature({ geometry: new Circle(centerProj, radius) });
  circleFeature.setStyle(style);
  features.push(circleFeature);

  return features;
}

// Gère les marqueurs de points de grille sur la carte principale : un seul
// marqueur par point de grille distinct (peu importe combien de requêtes/clics
// y ont mené), le point courant en rouge, les points visités précédemment en
// gris. Remplace my_circle()/WMS_request.circle() (cercles de rayon fixe,
// un par clic, jamais dédupliqués).
export class GridPointMarkers {
  constructor(map) {
    this.map = map;
    this.markers = []; // {model, lon, lat, axisU, axisV, source, layer}
    this.current = null;
  }

  // Marqueur temporaire au point cliqué (brut, avant résolution du point de
  // grille) — juste un retour visuel immédiat pendant que findGridAxes sonde
  // les voisins (quelques requêtes WMS). Retourne un handle .clear()
  // idempotent.
  pending([lon, lat]) {
    const style = new Style({
      stroke: new Stroke({ color: CURRENT_COLOR, width: 5 }),
    });
    const feature = new Feature({
      geometry: new Circle(fromLonLat([lon, lat]), PENDING_RADIUS_M),
    });
    feature.setStyle(style);
    const source = new sourceVector({ features: [feature] });
    const layer = new layerVector({ source });
    this.map.addLayer(layer);
    let cleared = false;
    return {
      clear: () => {
        if (cleared) return;
        cleared = true;
        this.map.removeLayer(layer);
      },
    };
  }

  // Ne matche que des marqueurs du même modèle : deux modèles ont des
  // grilles différentes (résolution, orientation), donc un point HRDPS et
  // un point RDPS proches l'un de l'autre restent deux points distincts.
  _findMarker(model, lon, lat, resolutionKm) {
    return this.markers.find(
      m => m.model === model && distanceKm(lat, lon, m.lat, m.lon) <= 0.5 * resolutionKm
    );
  }

  _redraw(marker, color) {
    marker.source.clear();
    marker.source.addFeatures(
      meshFeatures(marker.lon, marker.lat, marker.axisU, marker.axisV, color)
    );
    marker.layer.setZIndex(color === CURRENT_COLOR ? CURRENT_ZINDEX : PAST_ZINDEX);
  }

  // Affiche/replace le maillage rouge sur le point de grille (lon, lat) du
  // modèle donné, réutilisant le marqueur existant du même modèle s'il y en
  // a déjà un à moins de 0.5 * resolutionKm (même point de grille revisité),
  // et repasse l'ancien point courant en gris.
  promote(model, lon, lat, axisU, axisV, resolutionKm) {
    let marker = this._findMarker(model, lon, lat, resolutionKm);
    if (marker) {
      marker.lon = lon;
      marker.lat = lat;
      marker.axisU = axisU;
      marker.axisV = axisV;
    } else {
      const source = new sourceVector({ projection: 'EPSG:4326' });
      const layer = new layerVector({ source });
      this.map.addLayer(layer);
      marker = { model, lon, lat, axisU, axisV, source, layer };
      this.markers.push(marker);
    }
    if (this.current && this.current !== marker) {
      this._redraw(this.current, PAST_COLOR);
    }
    this._redraw(marker, CURRENT_COLOR);
    this.current = marker;
  }

  // Retire tous les marqueurs gris de la carte, en gardant seulement le
  // point courant (s'il y en a un).
  clearPast() {
    for (const marker of this.markers) {
      if (marker !== this.current) {
        this.map.removeLayer(marker.layer);
      }
    }
    this.markers = this.current ? [this.current] : [];
  }
}
